/**
 * Spec 052, step 6: a review of a client's order's code — the same decision as a task's code
 * review (components/TaskCode), on the order's own routes. The order's owner approves the shown
 * version or sends it back with a reason; the unit order's doer merges it after.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { OrderCode, type OrderCodeReview } from "@/api/orders";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { SelectField, TextArea } from "@/components/ui/Field";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { EmptyNote } from "@/kit";
import { nameOf, usePeople } from "@/lib/people";

export const REVIEW_LABEL = { pending: "Ждёт проверки", approved: "Согласовано", rejected: "Нужны исправления", withdrawn: "Отозвано", merged: "Слито" } as const;

export function useOrderCodeRefresh(orderId: string) {
  const qc = useQueryClient();
  return () => {
    for (const key of [["order-code", orderId], ["order-code-reviews-for-me"]]) void qc.invalidateQueries({ queryKey: key });
  };
}

export function OrderCodeReviewPanel({ orderId, item: given, me }: { orderId: string; item: OrderCodeReview; me?: string }) {
  const refresh = useOrderCodeRefresh(orderId);
  const toast = useToast();
  const people = usePeople();
  const [comment, setComment] = useState("");
  const [path, setPath] = useState("");
  const ref = { id: given.id, unit_order_id: given.unit_order_id };
  const review = useQuery({ queryKey: ["order-code", orderId, "review", given.id], queryFn: () => OrderCode.review(orderId, ref), refetchInterval: 15_000 });
  const diff = useQuery({ queryKey: ["order-code", orderId, "review-diff", given.id, path], queryFn: () => OrderCode.reviewDiff(orderId, ref, path) });
  const action = useMutation({
    mutationFn: (kind: "approve" | "reject") => OrderCode.decide(orderId, { ...review.data!, unit_order_id: given.unit_order_id }, kind, comment.trim()),
    onSuccess: (item) => { toast.success(REVIEW_LABEL[item.status]); refresh(); },
    onError: (error: Error) => { toast.error(error.message); refresh(); },
  });
  if (review.isLoading) return <SkeletonList count={1} />;
  if (!review.data) return <EmptyNote action={<Button onClick={() => void review.refetch()}>Повторить</Button>}>Не удалось загрузить ревью.</EmptyNote>;
  const item = review.data;
  const mine = me === item.reviewer_id && item.status === "pending";
  // The server kept only a checked version for review; a stale one needs a new check first.
  const ready = item.stale === false && !review.isError && !diff.isError && !!diff.data;
  return (
    <Card className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold">{REVIEW_LABEL[item.status]}</p>
        <span className="text-meta text-p-text-muted">Проверяет {nameOf(people.data, item.reviewer_id) ?? "владелец заказа"}</span>
      </div>
      <p className="whitespace-pre-wrap text-body">{item.summary}</p>
      <p className="text-meta text-p-text-muted">Версия {item.head.slice(0, 8)} · основа {item.base.slice(0, 8)}</p>
      {review.isError && <p role="alert" className="text-p-blocked">Не удалось обновить состояние. Действия недоступны до восстановления связи.</p>}
      {item.stale && <p role="status" className="rounded-xl bg-p-blocked-tint p-3 text-body">Код изменился после проверки. Нужна новая проверка и новое ревью. {item.stale_reason}</p>}
      <details open className="space-y-2">
        <summary className="min-h-11 cursor-pointer py-2 font-semibold">Изменения этой версии</summary>
        {diff.data?.files && diff.data.files.length > 1 && (
          <SelectField label="Файл" value={path} onChange={setPath} placeholder="Все файлы" options={diff.data.files.map((file) => ({ value: file, label: file }))} />
        )}
        {diff.isLoading && <SkeletonList count={1} />}
        {diff.isError && <EmptyNote action={<Button onClick={() => void diff.refetch()}>Повторить</Button>}>Изменения не загрузились.</EmptyNote>}
        {diff.data && (
          <pre tabIndex={0} aria-label="Изменения кода" className="max-h-[28rem] overflow-auto whitespace-pre-wrap break-all rounded-xl border border-p-border bg-p-surface-2 p-3 text-xs leading-relaxed">
            {diff.data.diff || "В этой версии нет изменений файлов."}
          </pre>
        )}
        {diff.data?.truncated && <p className="text-meta text-p-blocked">Показана часть изменений. Выбери отдельный файл.</p>}
      </details>
      {item.comment && <p className="whitespace-pre-wrap text-body">Решение: {item.comment}</p>}
      {item.user_confirmation && <p className="whitespace-pre-wrap text-meta text-p-text-muted">Решение передал агент со слов проверяющего: «{item.user_confirmation}»</p>}
      {mine && (
        <>
          <TextArea label="Комментарий к решению" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={4000} hint="Для возврата на исправление укажи причину." />
          <div className="flex flex-wrap gap-2">
            <Button disabled={!ready || action.isPending} onClick={() => action.mutate("approve")}>Согласовать эту версию</Button>
            <Button variant="secondary" disabled={!comment.trim() || action.isPending} onClick={() => action.mutate("reject")}>На исправление</Button>
          </div>
          <p className="text-meta text-p-text-muted">Согласование разрешает исполнителю поручения слить эту версию в основную ветку заказа.</p>
        </>
      )}
      {item.merge_commit && <p className="text-meta">Слито в основную ветку: {item.merge_commit.slice(0, 12)}.</p>}
      {action.isPending && <p role="status" className="text-meta">Сохраняем решение…</p>}
    </Card>
  );
}
